import React, { useEffect, useState } from "react";
import { Modal, Button, Form, Alert, Spinner } from "react-bootstrap";
import EnrollmentService from "../../../_services/EnrollmentService";

interface ReversePaymentModalProps {
  show: boolean;
  onHide: () => void;
  enrollmentId: number;
  payment: any; // the payment row being reversed
  onSuccess: () => void;
}

interface CancellationReason {
  id: number;
  name: string;
}

const ReversePaymentModal: React.FC<ReversePaymentModalProps> = ({
  show,
  onHide,
  enrollmentId,
  payment,
  onSuccess,
}) => {
  const [reasons, setReasons] = useState<CancellationReason[]>([]);
  const [cancellationReasonId, setCancellationReasonId] = useState<number | null>(null);
  const [remarks, setRemarks] = useState("");
  const [reversalDate, setReversalDate] = useState(
    new Date().toISOString().slice(0, 10),
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  useEffect(() => {
    if (show) {
      setError(null);
      setSuccessMsg(null);
      setRemarks("");
      setCancellationReasonId(null);
      EnrollmentService.getCancellationReasons()
        .then((res: any) => {
          if (res.data?.success) setReasons(res.data.data);
        })
        .catch(console.error);
    }
  }, [show]);

  const handleSubmit = async () => {
    setError(null);
    if (!remarks.trim()) {
      setError("Remarks are required for reversal");
      return;
    }

    setSubmitting(true);
    try {
      // Full-receipt reversal: send only the parent paymentId (NOT paymentDetailId)
      // so the backend reverses ALL detail lines and flags the original reversed=1.
      // Sending paymentDetailId would trigger the single-line path, which reverses
      // just one detail line and leaves the receipt mostly "paid".
      const res = await EnrollmentService.reversePayment(enrollmentId, {
        paymentId: payment.paymentId,
        cancellationReasonId,
        remarks: remarks.trim(),
        reversalDate,
      });
      if (res.data?.success) {
        setSuccessMsg("Payment reversed successfully");
        onSuccess();
      } else {
        setError(res.data?.message || "Failed to reverse payment");
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || "Failed to reverse payment");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: 16 }}>Reverse Payment</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ fontSize: 13 }}>
        {error && <Alert variant="danger" style={{ fontSize: 12 }}>{error}</Alert>}
        {successMsg && (
          <Alert variant="success" style={{ fontSize: 12 }}>{successMsg}</Alert>
        )}

        {payment && (
          <div
            style={{
              background: "var(--surface-page)",
              padding: 10,
              borderRadius: 4,
              marginBottom: 12,
              fontSize: 12,
            }}
          >
            <div>
              <strong>Receipt:</strong> {payment.receiptNumber}
            </div>
            <div>
              <strong>Date:</strong> {payment.paymentDate}
            </div>
            <div>
              <strong>Amount:</strong> {payment.amount?.toLocaleString()}
            </div>
            <div>
              <strong>Head:</strong> {payment.headName}
            </div>
          </div>
        )}

        <Form.Group className="mb-2">
          <Form.Label style={{ fontSize: 12, fontWeight: 600 }}>
            Reversal Date
          </Form.Label>
          <Form.Control
            type="date"
            size="sm"
            value={reversalDate}
            onChange={(e) => setReversalDate(e.target.value)}
          />
        </Form.Group>

        <Form.Group className="mb-2">
          <Form.Label style={{ fontSize: 12, fontWeight: 600 }}>
            Reason
          </Form.Label>
          <Form.Select
            size="sm"
            value={cancellationReasonId || ""}
            onChange={(e) =>
              setCancellationReasonId(Number(e.target.value) || null)
            }
          >
            <option value="">-- Select Reason --</option>
            {reasons.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </Form.Select>
        </Form.Group>

        <Form.Group className="mb-2">
          <Form.Label style={{ fontSize: 12, fontWeight: 600 }}>
            Remarks *
          </Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            size="sm"
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            placeholder="Enter reason for reversal..."
          />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        {successMsg ? (
          <Button variant="secondary" size="sm" onClick={onHide}>
            Close
          </Button>
        ) : (
          <>
            <Button variant="secondary" size="sm" onClick={onHide}>
              Cancel
            </Button>
            <Button
              variant="danger"
              size="sm"
              onClick={handleSubmit}
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <Spinner role="status" animation="border" size="sm" /> Reversing...
                </>
              ) : (
                "Reverse Payment"
              )}
            </Button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default ReversePaymentModal;
